import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Contract } from "interface/contract";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";

interface ContractsMobileListProps {
	items: Contract[];
	loading: boolean;
	onConfirm: (item: Contract) => Promise<void>;
	onOpenPdf: (item: Contract) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (statusId: number) => {
	if (statusId === 5) return "contract-list-card--status-green";
	if (statusId === 4) return "contract-list-card--status-orange";
	return "contract-list-card--status-red";
};

const ContractsMobileList: FC<ContractsMobileListProps> = ({
	items,
	loading,
	onConfirm,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [confirmingIds, setConfirmingIds] = useState<number[]>([]);

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	const handleConfirm = (item: Contract) => {
		if (item.showSign !== 1 || confirmingIds.includes(item.id)) {
			return;
		}

		setConfirmingIds((prev) => [...prev, item.id]);
		onConfirm(item).finally(() => {
			setConfirmingIds((prev) => prev.filter((id) => id !== item.id));
		});
	};

	return (
		<div className="contract-list-mobile" onScroll={handleScroll}>
			{empty && <div className="contract-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const confirming = confirmingIds.includes(item.id);
				const canConfirm = item.showSign === 1;
				const itemKey = `${item.id}-${item.statusId}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`contract-list-card ${getStatusClassName(item.statusId)}`}
					>
						<div className="contract-list-card-header">
							<div className="contract-list-card-note">{item.id}</div>
							<div className="contract-list-card-vessel">{item.vesselName || "-"}</div>
							<div className="contract-list-card-date">
								{item.registrationTime ? (
									<GrideDateFormatter
										date={item.registrationTime}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="contract-list-card-body">
							<div>
								<span>{t("ContractNoteNumber")}</span>
								<strong>{item.id}</strong>
							</div>
							<div>
								<span>{t("RegistrationId")}</span>
								<strong>{item.registrationId || "-"}</strong>
							</div>
							<div>
								<span>{t("Package")}</span>
								<strong>{item.packageId || "-"}</strong>
							</div>
							<div>
								<span>Status</span>
								<strong>{item.status || "-"}</strong>
							</div>
							<div>
								<span>{t("BuyerName")}</span>
								<strong>{item.buyerName || "-"}</strong>
							</div>
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{item.vesselRegistrationId || "-"}</strong>
							</div>
							<div className="contract-list-card-text-block">
								<span>{t("Information")}</span>
								<strong>{item.information || "-"}</strong>
							</div>
						</div>

						<div className="contract-list-card-actions">
							<button
								type="button"
								className="contract-list-icon-btn"
								onClick={() => handleConfirm(item)}
								disabled={!canConfirm || confirming}
								title={t("Confirm")}
							>
								<img src={`/icon/${ICON_NAMES.Confirmation}.svg`} alt={t("Confirm")} />
							</button>
							<button
								type="button"
								className="contract-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && <div className="contract-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default ContractsMobileList;